import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Archive, ArchiveRestore, Plus, X } from 'lucide-react';
import type { CustomFieldType, CustomFieldValue, CustomFieldView, FieldOption } from '@vwt/contracts';
import { Badge, Button, Checkbox, cn, Dialog, DialogContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, NativeSelect, toast } from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';
import { formatCalendarDate, formatNumber } from '../lib/format.js';
import { DateInput } from './date-input.js';
import { ColorPicker, LABEL_COLORS } from './labels.js';

const TYPES: CustomFieldType[] = ['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'SINGLE_SELECT', 'MULTI_SELECT'];
const DEFAULT_OPTION_COLORS: FieldOption['color'][] = ['blue', 'green', 'orange', 'purple', 'teal', 'pink', 'amber', 'indigo', 'red', 'gray'];
export const isSelectField = (field: Pick<CustomFieldView, 'type'>) => field.type === 'SINGLE_SELECT' || field.type === 'MULTI_SELECT';

/** Every custom field the reader can see (not archived); cached, definitions change seldom. */
const NO_FIELDS: CustomFieldView[] = [];
export function useCustomFields(): CustomFieldView[] {
  const query = useApiQuery('listCustomFields', { query: {} }, { staleTime: 5 * 60_000 });
  return query.data?.data ?? NO_FIELDS;
}

/** Document properties (ADR-0042): custom fields defined for documents instead of work. */
export function useDocumentProperties(): CustomFieldView[] {
  const query = useApiQuery('listCustomFields', { query: { scope: 'DOCUMENTS' as const } }, { staleTime: 5 * 60_000 });
  return query.data?.data ?? NO_FIELDS;
}

/** Fields that apply to work in a project: the global ones plus that project's own. */
export function fieldsFor(fields: CustomFieldView[], projectId: string | null | undefined): CustomFieldView[] {
  return fields.filter((f) => !f.archived && (f.scope === 'GLOBAL' || (projectId && f.project?.id === projectId)));
}

function OptionChip({ option }: { option: FieldOption }) {
  return (
    <span className="inline-flex max-w-40 items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs font-medium whitespace-nowrap text-fg-muted">
      <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: LABEL_COLORS[option.color] }} aria-hidden />
      <span className="truncate">{option.label}</span>
    </span>
  );
}

/** Read-only rendering of a stored value. */
export function FieldValue({ field, value }: { field: CustomFieldView; value: CustomFieldValue | undefined }) {
  const { t } = useTranslation();
  if (value === undefined || value === null || (Array.isArray(value) && !value.length)) return <span className="text-fg-subtle">—</span>;
  switch (field.type) {
    case 'BOOLEAN':
      return <span>{value ? t('common.yes') : t('common.no')}</span>;
    case 'NUMBER':
      return <span className="tabular-nums">{typeof value === 'number' ? formatNumber(value) : String(value)}</span>;
    case 'DATE':
      return <span>{formatCalendarDate(String(value), { day: 'numeric', month: 'short', year: 'numeric' })}</span>;
    case 'SINGLE_SELECT': {
      const option = field.options.find((o) => o.id === value);
      return option ? <OptionChip option={option} /> : <span className="text-fg-subtle">—</span>;
    }
    case 'MULTI_SELECT':
      return (
        <span className="flex flex-wrap gap-1">
          {(Array.isArray(value) ? value : []).map((id) => {
            const option = field.options.find((o) => o.id === id);
            return option ? <OptionChip key={id} option={option} /> : null;
          })}
        </span>
      );
    default:
      return <span className="truncate">{String(value)}</span>;
  }
}

/**
 * Editor for one value. Text and numbers save when leaving the field or pressing Enter; the other
 * kinds save on choice. onCommit(null) clears the value.
 */
export function FieldEditor({ field, value, onCommit, disabled, className, label }: { field: CustomFieldView; value: CustomFieldValue | undefined; onCommit: (value: CustomFieldValue | null) => void; disabled?: boolean; className?: string; label?: string }) {
  const name = label ?? field.name;
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value === undefined || value === null ? '' : String(value));
  useEffect(() => {
    setDraft(value === undefined || value === null ? '' : String(value));
  }, [value]);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const base = cn('h-7 min-w-0', className);
  switch (field.type) {
    case 'TEXT':
    case 'NUMBER': {
      const commit = () => {
        const text = draft.trim();
        const current = value === undefined || value === null ? '' : String(value);
        if (text === current) return;
        if (!text) return onCommit(null);
        if (field.type === 'NUMBER') {
          const n = Number(text.replace(',', '.'));
          if (!Number.isFinite(n)) {
            setDraft(current);
            return;
          }
          return onCommit(n);
        }
        onCommit(text);
      };
      return (
        <Input
          className={base}
          aria-label={name}
          disabled={disabled}
          inputMode={field.type === 'NUMBER' ? 'decimal' : undefined}
          value={draft}
          maxLength={500}
          onClick={stop}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
        />
      );
    }
    case 'BOOLEAN':
      return (
        <NativeSelect className={base} aria-label={name} disabled={disabled} value={value === true ? 'true' : value === false ? 'false' : ''} onClick={stop} onChange={(e) => onCommit(e.target.value === '' ? null : e.target.value === 'true')}>
          <option value="">—</option>
          <option value="true">{t('common.yes')}</option>
          <option value="false">{t('common.no')}</option>
        </NativeSelect>
      );
    case 'DATE':
      return (
        <span onClick={stop} className="contents">
          <DateInput className={base} aria-label={name} disabled={disabled} value={typeof value === 'string' ? value : null} onCommit={onCommit} />
        </span>
      );
    case 'SINGLE_SELECT':
      return (
        <NativeSelect className={base} aria-label={name} disabled={disabled} value={typeof value === 'string' ? value : ''} onClick={stop} onChange={(e) => onCommit(e.target.value || null)}>
          <option value="">—</option>
          {field.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </NativeSelect>
      );
    case 'MULTI_SELECT': {
      const chosen = Array.isArray(value) ? value : [];
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <button type="button" onClick={stop} aria-label={name} className={cn('flex min-h-7 w-full flex-wrap items-center gap-1 rounded-[var(--radius)] border border-border-strong bg-surface px-1.5 py-0.5 text-left text-xs', className)}>
              {chosen.length ? <FieldValue field={field} value={chosen} /> : <span className="text-fg-subtle">—</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" onClick={stop}>
            {field.options.map((o) => (
              <DropdownMenuItem
                key={o.id}
                onSelect={(e) => {
                  e.preventDefault();
                  const next = chosen.includes(o.id) ? chosen.filter((c) => c !== o.id) : [...chosen, o.id];
                  onCommit(next.length ? next : null);
                }}
              >
                <Checkbox checked={chosen.includes(o.id)} aria-hidden tabIndex={-1} /> <OptionChip option={o} />
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    default:
      return null;
  }
}

/** Options editor for select fields: add, rename, recolor, remove (stored values keep removed ids). */
function OptionsEditor({ options, onChange }: { options: Array<{ id?: string; label: string; color?: FieldOption['color'] }>; onChange: (next: Array<{ id?: string; label: string; color?: FieldOption['color'] }>) => void }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const add = () => {
    const text = label.trim();
    if (!text || options.some((o) => o.label.toLowerCase() === text.toLowerCase())) return;
    // Same default order as the server, so what is shown is what gets saved.
    onChange([...options, { label: text, color: DEFAULT_OPTION_COLORS[options.length % DEFAULT_OPTION_COLORS.length] }]);
    setLabel('');
  };
  return (
    <div className="flex flex-col gap-1.5">
      <ul className="flex flex-col gap-1">
        {options.map((o, i) => (
          <li key={o.id ?? `new-${i}`} className="flex items-center gap-2">
            <Input
              className="h-7 flex-1"
              defaultValue={o.label}
              maxLength={60}
              aria-label={t('fields.optionName')}
              onBlur={(e) => {
                const text = e.target.value.trim();
                if (text && text !== o.label) onChange(options.map((x, j) => (j === i ? { ...x, label: text } : x)));
              }}
            />
            <ColorPicker value={o.color ?? 'gray'} onChange={(color) => onChange(options.map((x, j) => (j === i ? { ...x, color } : x)))} label={t('labels.color')} />
            <Button size="iconSm" variant="ghost" aria-label={t('fields.removeOption', { name: o.label })} onClick={() => onChange(options.filter((_, j) => j !== i))}>
              <X />
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <Input
          className="h-7 flex-1"
          value={label}
          maxLength={60}
          placeholder={t('fields.newOption')}
          aria-label={t('fields.newOption')}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button size="sm" variant="secondary" onClick={add} disabled={!label.trim()}>
          <Plus /> {t('fields.addOption')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Manager for the fields of one scope: all work (projectId omitted), one project, or document
 * properties. Creating, renaming, options, order and archive; values are never deleted.
 */
export function FieldManagerDialog({ projectId, documents, onClose }: { projectId?: string; documents?: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const list = useApiQuery('listCustomFields', { query: { includeArchived: 'true' as const, ...(projectId ? { projectId } : {}), ...(documents ? { scope: 'DOCUMENTS' as const } : {}) } });
  const invalidate: Array<'listCustomFields' | 'getDocument' | 'listDocumentIndex' | 'listWorkItems' | 'getWorkItem'> = documents ? ['listCustomFields', 'getDocument', 'listDocumentIndex'] : ['listCustomFields', 'listWorkItems', 'getWorkItem'];
  const create = useApiMutation('createCustomField', { invalidate });
  const update = useApiMutation('updateCustomField', { invalidate });
  const [name, setName] = useState('');
  const [type, setType] = useState<CustomFieldType>('SINGLE_SELECT');
  const [options, setOptions] = useState<Array<{ id?: string; label: string; color?: FieldOption['color'] }>>([]);
  const fields = (list.data?.data ?? []).filter((f) => (documents ? f.scope === 'DOCUMENTS' : projectId ? f.scope === 'PROJECT' && f.project?.id === projectId : f.scope === 'GLOBAL'));
  const active = fields.filter((f) => !f.archived);
  const patch = async (field: CustomFieldView, body: Omit<Parameters<typeof update.mutateAsync>[0]['body'], 'expectedVersion'>) => {
    try {
      await update.mutateAsync({ params: { id: field.id }, body: { expectedVersion: field.version, ...body } });
    } catch (error) {
      toastError(error);
    }
  };
  const move = (field: CustomFieldView, delta: number) => {
    const order = [...active];
    const index = order.findIndex((f) => f.id === field.id);
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target]!, order[index]!];
    void Promise.all(order.map((f, i) => (f.position !== i ? patch(f, { position: i }) : null)));
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={documents ? t('fields.documentsTitle') : projectId ? t('fields.projectTitle') : t('fields.title')} description={documents ? t('fields.documentsHint') : t('fields.hint')} closeLabel={t('common.close')} className="w-[min(640px,calc(100vw-2rem))]">
        <form
          className="flex flex-col gap-2 border-b border-border pb-3"
          onSubmit={async (e) => {
            e.preventDefault();
            // The dialog can open from inside another form (project settings); keep the submit here.
            e.stopPropagation();
            if (!name.trim()) return;
            try {
              await create.mutateAsync({ idempotencyKey: newIdempotencyKey(), body: { name: name.trim(), type, ...(projectId ? { projectId } : {}), ...(documents ? { scope: 'DOCUMENTS' as const } : {}), ...(isSelectField({ type }) ? { options } : {}) } });
              toast.success(t('fields.created'));
              setName('');
              setOptions([]);
            } catch (error) {
              toastError(error);
            }
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-40 flex-1 flex-col gap-1 text-xs font-medium text-fg-muted">
              {t('fields.name')}
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-fg-muted">
              {t('fields.type')}
              <NativeSelect value={type} onChange={(e) => setType(e.target.value as CustomFieldType)}>
                {TYPES.map((ft) => (
                  <option key={ft} value={ft}>
                    {t(`fields.types.${ft}`)}
                  </option>
                ))}
              </NativeSelect>
            </label>
            <Button type="submit" variant="primary" size="sm" disabled={!name.trim() || (isSelectField({ type }) && !options.length)} loading={create.isPending}>
              {t('fields.add')}
            </Button>
          </div>
          {isSelectField({ type }) ? <OptionsEditor options={options} onChange={setOptions} /> : null}
        </form>
        {fields.length ? (
          <ul className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
            {fields.map((f) => (
              <li key={f.id} className={cn('flex flex-col gap-2 rounded-[var(--radius)] border border-border px-3 py-2', f.archived && 'opacity-60')}>
                <div className="flex items-center gap-2">
                  <Input className="h-7 flex-1" defaultValue={f.name} maxLength={60} aria-label={t('fields.name')} disabled={!f.canManage} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== f.name && void patch(f, { name: e.target.value.trim() })} />
                  <Badge tone="outline">{t(`fields.types.${f.type}`)}</Badge>
                  {f.archived ? <Badge tone="outline">{t('fields.archived')}</Badge> : null}
                  {f.canManage && !f.archived ? (
                    <>
                      <Button size="iconSm" variant="ghost" aria-label={t('fields.moveUp')} onClick={() => move(f, -1)}>
                        <ArrowUp />
                      </Button>
                      <Button size="iconSm" variant="ghost" aria-label={t('fields.moveDown')} onClick={() => move(f, 1)}>
                        <ArrowDown />
                      </Button>
                    </>
                  ) : null}
                  {f.canManage ? (
                    <Button size="iconSm" variant="ghost" aria-label={f.archived ? t('fields.restore') : t('fields.archive')} title={f.archived ? t('fields.restore') : t('fields.archive')} onClick={() => void patch(f, { archived: !f.archived })}>
                      {f.archived ? <ArchiveRestore /> : <Archive />}
                    </Button>
                  ) : null}
                </div>
                {isSelectField(f) && f.canManage && !f.archived ? <OptionsEditor options={f.options} onChange={(next) => void patch(f, { options: next })} /> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-fg-subtle">{t('fields.empty')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
